import { getUserToken } from '@/lib/utils/misc'
import type { IBar } from '@/lib/interfaces/bar'
import { ONBOARDING_START_DATE } from '@/config'

// Hitos del funnel de onboarding que el cliente puede reportar al endpoint
// `/api/onboarding/milestone`. Mantener alineado con `ALLOWED_MILESTONES` en
// `src/app/api/onboarding/milestone/route.ts` y con el subdoc
// `Bar.onboardingMilestones` (modelo + interface).
export type OnboardingMilestone =
  | 'welcomeShownAt'
  | 'welcomeDismissedAt'
  | 'arrivedToEditorFirstTimeAt'
  | 'clickedOnItemFirstTimeAt'
  | 'editedItemFirstTimeAt'
  | 'updatedMenuFirstTimeAt'
  | 'menuEditedFirstTimeAt'
  | 'visitedPublicMenuFirstTimeAt'

interface FirebaseLikeUser {
  getIdToken: () => Promise<string>
}

/**
 * Indica si el `barData` que tiene el cliente ya tiene fijado un hito. Lo
 * usamos para cortar en seco la petición al endpoint cuando sabemos que el
 * server no va a escribir nada (el `findOneAndUpdate` filtra por `field:
 * null`). Es solo una optimización para no spammear el backend: la
 * fuente de verdad sigue siendo el doc del bar, así que si el cliente va
 * "stale" el server escribirá cuando le llegue la petición.
 *
 * Importante: `barData` viaja por `NextResponse.json(...)`, que serializa
 * `Date` a string ISO. Por eso aquí aceptamos tanto `Date` (en tests o
 * si el contexto se hidrata con objetos Mongoose crudos) como string ISO
 * (lo que llega en producción desde `/api/bar-data`).
 */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/

export const hasMilestone = (
  barData: IBar | null | undefined,
  milestone: OnboardingMilestone
): boolean => {
  const value = barData?.onboardingMilestones?.[milestone]
  if (value instanceof Date) return true
  if (typeof value === 'string' && ISO_DATE_RE.test(value)) return true
  return false
}

/**
 * Dispara un hito del funnel de onboarding contra
 * `/api/onboarding/milestone`. Fire-and-forget: nunca lanza, nunca bloquea.
 * El endpoint se encarga de hacer el `findOneAndUpdate` idempotente en el
 * doc del bar y, solo en el primer hit de cada hito, enviar el evento a
 * GA4.
 *
 * @param milestone Nombre del hito. Debe estar en `OnboardingMilestone` y en
 *   el whitelist del endpoint.
 * @param metadata No se usa todavía (reservado para parámetros extra en GA4).
 * @param token Firebase ID token. Se pasa explícitamente para no acoplar el
 *   helper al `useAuthContext` cuando el call-site ya tiene el token.
 */
export const trackOnboardingMilestone = (
  milestone: OnboardingMilestone,
  metadata?: Record<string, unknown>,
  token?: string | null
): void => {
  sendMilestone(milestone, metadata, token)
}

const sendMilestone = async (
  milestone: OnboardingMilestone,
  metadata: Record<string, unknown> | undefined,
  token: string | null | undefined
): Promise<void> => {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (token) headers.Authorization = `Bearer ${token}`
    await fetch('/api/onboarding/milestone', {
      method: 'POST',
      headers,
      body: JSON.stringify({ milestone, metadata }),
      keepalive: true
    })
  } catch (err) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[trackOnboardingMilestone] failed', milestone, err)
    }
  }
}

/**
 * Convenience: igual que `trackOnboardingMilestone` pero obtiene el token
 * automáticamente del `user` de Firebase Auth. Útil en componentes
 * client-side que ya tienen `useAuthContext`.
 */
export const trackOnboardingMilestoneForUser = async (
  user: FirebaseLikeUser | null | undefined,
  milestone: OnboardingMilestone,
  metadata?: Record<string, unknown>
): Promise<void> => {
  if (!user) {
    trackOnboardingMilestone(milestone, metadata, null)
    return
  }
  try {
    const token = await getUserToken(user as Parameters<typeof getUserToken>[0])
    trackOnboardingMilestone(milestone, metadata, token)
  } catch {
    trackOnboardingMilestone(milestone, metadata, null)
  }
}

/**
 * Variante "smart": hace un short-circuit con `barData` para no enviar la
 * petición cuando el cliente ya sabe que el hito está fijado. La fuente de
 * verdad sigue siendo el server (filtro `field: null`), así que un cliente
 * stale acabará reportando cuando refresque; pero en el caso típico
 * (hito ya cumplido y `barData` al día) nos ahorramos el roundtrip.
 *
 * Si no hay `user` o el token no se puede obtener, se intenta igualmente
 * sin Authorization: el middleware rechazará con 401 y no se escribirá
 * nada. Llamar sin `barData` también dispara (no asumimos nada).
 */
export const trackOnboardingMilestoneIfNotSet = async (
  barData: IBar | null | undefined,
  user: FirebaseLikeUser | null | undefined,
  milestone: OnboardingMilestone,
  metadata?: Record<string, unknown>
): Promise<void> => {
  if (hasMilestone(barData, milestone)) return
  // Guard de cohorte: solo los bares creados con el nuevo onboarding
  // (desde `ONBOARDING_START_DATE`) reportan hitos. Si `barData` no trae
  // `createdAt` (no hay bar autenticado) no asumimos nada y dejamos que el
  // backend decida con su propio guard.
  const createdAt = barData?.createdAt
  if (createdAt && new Date(createdAt).getTime() < ONBOARDING_START_DATE.getTime()) return
  await trackOnboardingMilestoneForUser(user, milestone, metadata)
}
